import { toIntlLocale } from './locale';

/** Деньги по ТЗ COM-08: `amount` — целое число минимальных единиц (копейки, центы). */
export interface Money {
  amount: number;
  currency: string;
}

const formatterCache = new Map<string, Intl.NumberFormat>();
const DEFAULT_MINOR_DIGITS = 2;

function getFormatter(locale: string, currency: string, fractionDigits: number): Intl.NumberFormat {
  const key = `${locale}|${currency}|${fractionDigits}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/** Число знаков минимальной единицы валюты (RUB, EUR — 2) по данным Intl. */
export function minorDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? DEFAULT_MINOR_DIGITS
    );
  } catch {
    // Конфигурация рынка ещё не загружена (пустой код валюты) — копейки и центы по умолчанию.
    return DEFAULT_MINOR_DIGITS;
  }
}

/**
 * Форматирование суммы для отображения (ТЗ COM-08):
 * RU — «1 290 ₽» (неразрывные пробелы), World — en «€12.90», es «12,90 €».
 * Дробная часть показывается, только если она есть: 129000 копеек → «1 290 ₽», 1290 центов → «€12.90».
 * `language` — язык интерфейса (ru | en | es) или готовая локаль Intl.
 */
export function formatMoney(money: Money, language: string): string {
  const digits = minorDigits(money.currency);
  const divisor = 10 ** digits;
  const hasFraction = money.amount % divisor !== 0;
  const formatter = getFormatter(toIntlLocale(language), money.currency, hasFraction ? digits : 0);
  return formatter.format(money.amount / divisor);
}

/** Сумма в основных единицах из поля ввода («6 500», «12,90») → минимальные единицы (COM-08). */
export function toMinorUnits(major: number, currency: string): number {
  return Math.round(major * 10 ** minorDigits(currency));
}

/** Минимальные единицы → число основных единиц для поля ввода (650000 копеек → 6500). */
export function fromMinorUnits(amount: number, currency: string): number {
  return amount / 10 ** minorDigits(currency);
}

/** Символ валюты для подписи у поля ввода суммы («₽ за ночь», «€ per night»). */
export function currencySymbol(currency: string, language: string): string {
  const parts = getFormatter(toIntlLocale(language), currency, 0).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? currency;
}
